import React, { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateActivity,
  checkTimeout,
  selectIsGloballyUnlocked,
  selectLockSession,
} from '../../store/slices/lockSessionSlice';
import { useIdleDetection } from '../../hooks/useIdleDetection';

// 检查间隔（每秒检查一次）
const CHECK_INTERVAL = 1000;

const LockSessionManager: React.FC = () => {
  const dispatch = useDispatch();
  const isGloballyUnlocked = useSelector(selectIsGloballyUnlocked);
  const lockSession = useSelector(selectLockSession);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // 处理用户活动
  const handleActivity = useCallback(() => {
    if (isGloballyUnlocked) {
      dispatch(updateActivity());
    }
  }, [dispatch, isGloballyUnlocked]);

  // 使用空闲检测 hook
  useIdleDetection({
    onActivity: handleActivity,
    throttleMs: 1000,
  });

  // 定期检查超时
  useEffect(() => {
    // 清除之前的定时器
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // 如果已解锁，启动定时器检查超时
    if (isGloballyUnlocked) {
      intervalRef.current = setInterval(() => {
        dispatch(checkTimeout());
      }, CHECK_INTERVAL);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [dispatch, isGloballyUnlocked]);

  // 监听页面可见性变化
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isGloballyUnlocked) {
        // 页面重新可见时检查超时
        dispatch(checkTimeout());
        // 更新活动时间
        dispatch(updateActivity());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dispatch, isGloballyUnlocked]);

  // 监听窗口焦点变化
  useEffect(() => {
    const handleFocus = () => {
      if (isGloballyUnlocked) {
        dispatch(checkTimeout());
        dispatch(updateActivity());
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [dispatch, isGloballyUnlocked]);

  // 这个组件不渲染任何内容
  return null;
};

export default LockSessionManager;
